import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState('');
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already dismissed (check last 7 days)
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show if dismissed within last 7 days
      }
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

    if (isIOSDevice) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    }

    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Show prompt only on mobile, not already installed, and appropriate browser
    if ((isIOSDevice || isAndroid) && !standalone) {
      // For iOS, only show in Safari (PWA only works in Safari)
      if (isIOSDevice && isSafari) {
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      } else if (isAndroid) {
        // For Android, show after 2 seconds
        setTimeout(() => {
          setShowPrompt(true);
        }, 2000);
      }
    }

    // Listen for beforeinstallprompt event (Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      // Mark as permanently dismissed once installed
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
      console.log('[PWA] App installed successfully!');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (platform === 'android' && deferredPrompt) {
      // Show native install prompt for Android
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setShowPrompt(false);
      }
    }
    // For iOS, instructions are already shown
  }, [platform, deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    // Store timestamp instead of boolean for time-based re-prompting
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }, []);

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-black/20 to-transparent pointer-events-none">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-6 pointer-events-auto transform transition-all duration-300 ease-out animate-slide-up border border-blue-100">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Smartphone className="text-white" size={24} />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Install DST Energy Monitor
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Get quick access and work offline by installing our app on your device.
            </p>

            {platform === 'ios' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-blue-900 mb-3">
                  📱 Install on iPhone/iPad:
                </p>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Tap the <strong>Share</strong> button at the bottom of Safari</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Add"</strong> in the top right corner</li>
                </ol>
              </div>
            )}

            {platform === 'android' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-green-900 mb-3">
                  📱 Install on Android:
                </p>
                <ol className="text-sm text-green-800 space-y-2 list-decimal list-inside">
                  <li>Tap the button below or</li>
                  <li>Tap the <strong>menu (⋮)</strong> in your browser</li>
                  <li>Select <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></li>
                </ol>
              </div>
            )}

            <div className="flex gap-2">
              {platform === 'android' && deferredPrompt && (
                <button
                  onClick={handleInstallClick}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
                >
                  <Download size={18} />
                  Install Now
                </button>
              )}
              
              <button
                onClick={handleDismiss}
                className="px-4 py-2.5 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                {platform === 'android' && deferredPrompt ? 'Maybe Later' : 'Got It'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
